'use client'

import TableDataFooter from '@/shared/components/TableDataFooter'
import CreateDialog from '@/shared/components/categories/CreateDialog'
import EditDialog from '@/shared/components/categories/EditDialog'
import MenuDropdown from '@/shared/components/categories/MenuDropdown'
import { useAddDepartment } from '@/shared/hooks/categories/department/useAdd'
import { useDeleteDepartment, useDeleteDepartments } from '@/shared/hooks/categories/department/useDelete'
import { useUpdateDepartment } from '@/shared/hooks/categories/department/useUpdate'
import { useDebounce } from '@/shared/hooks/useDebounce'
import TabLayout, { ActionButton } from '@/shared/layouts/TabLayout'
import { api } from '@/shared/lib/api'
import { cn } from '@workspace/ui/lib/utils'
import { useCan } from '@/shared/lib/authorization'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useLoaderData, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { ColumnDef } from '@tanstack/react-table'
import { confirm } from '@workspace/ui/components/ConfirmDialog'
import { DataTable, DataTableSorting } from '@workspace/ui/components/DataTable'
import { BsSearchField } from '@workspace/ui/components/Searchfield'
import dayjs from 'dayjs'
import { PlusIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const searchSchema = z.object({
    q: z.string().optional(),
    page: z.number().catch(1).optional(),
    pageSize: z.number().catch(10).optional(),
    sortBy: z.string().optional(),
    sortDirection: z.string().optional(),
})

export const Route = createFileRoute('/categories/_pathlessLayout/departments')({
    validateSearch: searchSchema,
    component: CategoriesPage,
})

interface Department {
    id: string
    code: string
    name: string
    description: string
    isActive: boolean
    createdAt: string
}

function CategoriesPage() {
    const { links } = useLoaderData({ from: '/categories/_pathlessLayout' })
    const { t } = useTranslation()
    const searchParams = Route.useSearch()
    const navigate = useNavigate({ from: Route.fullPath })

    const updateSearch = (newSearch: Record<string, any>) => {
        navigate({ search: (prev: any) => ({ ...prev, ...newSearch }) })
    }

    const searchQuery = searchParams.q || ''
    const page = searchParams.page || 1
    const pageSize = searchParams.pageSize || 10
    const sorting = searchParams.sortBy
        ? { sortBy: searchParams.sortBy, sortDirection: searchParams.sortDirection as 'asc' | 'desc' }
        : null

    const setSearchQuery = (q: string) => updateSearch({ q, page: 1 })
    const setPage = (page: number) => updateSearch({ page })
    const setPageSize = (pageSize: number) => updateSearch({ pageSize, page: 1 })
    const setSorting = (sort: DataTableSorting | null) => {
        if (!sort) {
            updateSearch({ sortBy: undefined, sortDirection: undefined, page: 1 })
        } else {
            updateSearch({ sortBy: sort.sortBy, sortDirection: sort.sortDirection, page: 1 })
        }
    }

    const [rowSelection, setRowSelection] = useState({})

    const updateDepartment = useUpdateDepartment()
    const deleteMutation = useDeleteDepartment()
    const bulkDeleteMutation = useDeleteDepartments()
    const addMutation = useAddDepartment()

    const debouncedSearch = useDebounce(searchQuery, 500)

    const { data, isFetching } = useQuery({
        queryKey: ['departments', debouncedSearch, page, pageSize, sorting],
        queryFn: () =>
            api.department.list({
                page,
                take: pageSize,
                q: debouncedSearch || undefined,
                order: sorting?.sortDirection?.toUpperCase() as 'ASC' | 'DESC',
                orderBy: sorting?.sortBy,
            }),
    })

    const [isCreateOpen, setCreateOpen] = useState(false)
    const [editOpen, setEditOpen] = useState<Department | boolean>(false)

    const selectedCount = Object.keys(rowSelection).length
    const selectedIds = Object.keys(rowSelection)

    const handleCreateDepartment = async (values: { name: string; code: string; description?: string }) => {
        await addMutation.mutateAsync({
            ...values,
            description: values.description || undefined,
            isActive: true,
        })
    }

    const handleEditClick = (row: Department) => {
        setEditOpen(row)
    }

    const handleDeleteClick = async (id: string) => {
        confirm({
            title: t('categories.deleteConfirm.title'),
            description: t('categories.deleteConfirm.description'),
            action: {
                label: t('common.confirmDialog.confirm'),
                onClick: async () => {
                    // Lưu số lượng items TRƯỚC KHI xóa
                    const currentItemCount = data?.data.length || 0
                    const isLastItemOnPage = currentItemCount === 1 && page > 1

                    await deleteMutation.mutateAsync({ id })

                    // Nếu xóa item cuối cùng ở page > 1, quay về page trước
                    if (isLastItemOnPage) {
                        setPage(page - 1)
                    }
                },
            },
            cancel: {
                label: t('common.confirmDialog.cancel'),
            },
            variant: 'destructive',
        })
    }

    const handleBulkDeleteConfirm = async () => {
        if (selectedCount === 0) return

        const currentItemCount = data?.data.length || 0
        const willPageBeEmpty = currentItemCount === selectedCount && page > 1

        await bulkDeleteMutation.mutateAsync({ ids: selectedIds })
        setRowSelection({})

        if (willPageBeEmpty) {
            setPage(page - 1)
        }
    }

    const handleUpdateDepartment = async (values: any) => {
        await updateDepartment.mutateAsync({
            id: values.id,
            data: {
                ...values,
                description: values.description || '',
            },
        })
    }

    const showActionColumn = useCan('operate', 'library')

    const columns = useMemo<ColumnDef<Department>[]>(
        () => [
            ...(showActionColumn
                ? [
                      {
                          id: 'action',
                          accessorKey: 'action',
                          header: '',
                          size: 50,
                          cell: ({ row }: any) => (
                              <MenuDropdown
                                  id={row.original.id}
                                  onEdit={() => handleEditClick(row.original)}
                                  onDelete={handleDeleteClick}
                              />
                          ),
                          enableSorting: false,
                      },
                  ]
                : []),
            {
                id: 'code',
                accessorKey: 'code',
                header: t('categories.createDepartment.code'),
                size: 200,
                meta: { className: 'px-2 py-2 text-sm' },
                cell: ({ getValue }) => <span className="text-sm">{getValue() as string}</span>,
                enableSorting: true,
            },
            {
                id: 'name',
                accessorKey: 'name',
                header: t('categories.table.department'),
                enableSorting: true,
                size: 400,
                meta: { className: 'px-2 py-2 text-sm' },
                cell: ({ row, getValue }) => (
                    <span className="text-sm cursor-pointer" onClick={() => handleEditClick(row.original)}>
                        {getValue() as string}
                    </span>
                ),
            },
            {
                id: 'description',
                accessorKey: 'description',
                header: t('categories.table.description'),
                meta: { className: 'px-2 py-2 text-sm' },
                size: 400,
                cell: ({ getValue }) => <span className="text-sm">{getValue() as string}</span>,
                enableSorting: false,
            },
            {
                id: 'createdAt',
                accessorKey: 'createdAt',
                header: t('categories.table.createdDate'),
                size: 140,
                meta: { className: 'px-2 py-2 text-sm' },
                cell: ({ getValue }) => {
                    const value = getValue() as string | undefined
                    return <span className="text-sm">{value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'}</span>
                },
            },
        ],
        [t, page, data?.data.length, showActionColumn],
    )

    const actions: ActionButton[] = [
        {
            text: t('categories.createButton'),
            icon: PlusIcon,
            onClick: () => setCreateOpen(true),
            variant: 'default',
        },
    ]

    return (
        <>
            <TabLayout
                className="h-[calc(100vh-150px)] min-h-[500px] overflow-hidden"
                title={t('categories.title')}
                linksTo={links}
                actions={actions}
            >
                <div className="flex flex-col gap-4 max-lg:gap-2 min-h-0 flex-1">
                    <div className="shrink-0">
                        <BsSearchField
                            autoTrim
                            className="h-10 text-sm bg-background rounded-lg w-80 max-lg:w-full"
                            containerClassName={'max-lg:w-full'}
                            value={searchQuery}
                            placeholder={t('categories.searchPlaceholder')}
                            onChange={value => setSearchQuery(value)}
                        />
                    </div>
                    <DataTable
                        data={data?.data || []}
                        columns={columns}
                        enableRowSelection={true}
                        rowSelection={rowSelection}
                        setRowSelection={setRowSelection}
                        enableSorting={true}
                        sorting={sorting}
                        setSorting={setSorting}
                        isLoading={isFetching}
                        containerClassName={cn(
                            'flex-1 overflow-y-auto min-h-0',
                            bulkDeleteMutation.isPending || deleteMutation.isPending
                                ? 'opacity-50 pointer-events-none'
                                : '',
                        )}
                        emptyString={t('common.emptyState')}
                    />
                    <div className="shrink-0">
                        <TableDataFooter
                            selectedCount={selectedCount}
                            onDelete={handleBulkDeleteConfirm}
                            deleteDisabled={selectedCount === 0 || bulkDeleteMutation.isPending}
                            totalCount={data?.meta.itemCount || 0}
                            currentPage={data?.meta.page || 1}
                            pageSize={pageSize}
                            onPageSizeChange={value => setPageSize(value || 10)}
                            totalPages={data?.meta.pageCount || 1}
                            onPageChange={setPage}
                            isLoading={bulkDeleteMutation.isPending}
                        />
                    </div>
                </div>
            </TabLayout>
            <CreateDialog
                isOpen={isCreateOpen}
                onOpenChange={setCreateOpen}
                onConfirm={handleCreateDepartment}
                type="department"
            />

            <EditDialog
                isOpen={!!editOpen}
                onOpenChange={setEditOpen}
                onConfirm={handleUpdateDepartment}
                type="department"
                value={editOpen}
                isLoading={updateDepartment.isPending}
            />
        </>
    )
}
